/**
 * frontend/src/pages/PublicPlaylist.jsx — /playlist/:slug (public, no sign-in)
 * SoundDAO Sprint 6A — a shared built playlist, playable signed-out.
 */

import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { formatDuration, playlistToQueue, publicPlaylist } from '../lib/archiveApi';
import { usePlayer } from '../player/PlayerContext';
import '../styles/archive.css';
import BrandMark from '../components/BrandMark';

export default function PublicPlaylist({ session }) {
  const { slug } = useParams();
  const { playQueue, isCurrent, toggle, status } = usePlayer();
  const [pl, setPl] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setPl(null);
    setError(null);
    publicPlaylist(slug)
      .then((d) => { if (alive) setPl(d.playlist); })
      .catch((e) => { if (alive) setError(e); });
    return () => { alive = false; };
  }, [slug]);

  useEffect(() => {
    if (pl) document.title = `${pl.title} · SoundDAO`;
    return () => { document.title = 'SoundDAO'; };
  }, [pl]);

  const queue = pl ? playlistToQueue(pl, 'public_playlist') : [];
  const totalSec = pl ? pl.tracks.reduce((s, t) => s + (t.durationSec || 0), 0) : 0;

  return (
    <div style={{ background: '#12112b', minHeight: '100vh' }}>
      {!session && (
        <header className="ax-pubhead">
          <Link to="/" className="ax-logo"><BrandMark /> SoundDAO</Link>
          <Link to="/login" className="ax-btn ax-btn--sm">Sign in</Link>
        </header>
      )}
      <div className="ax-page" style={{ minHeight: 0 }}>
        <div className="ax-wrap">
          {error && (
            <div className="ax-empty">
              {error.status === 404 ? 'This playlist is private or no longer exists.' : error.message}
              <div style={{ marginTop: 12 }}><Link to={session ? '/dig' : '/'} className="ax-btn">Dig the Archive</Link></div>
            </div>
          )}
          {!pl && !error && <><div className="ax-skel" style={{ height: 110 }} /><div className="ax-skel" /><div className="ax-skel" /></>}
          {pl && (
            <div className="ax-two">
              <div>
                <div className="ax-row" style={{ alignItems: 'flex-start', gap: 14, marginTop: 6 }}>
                  <div className="ax-cover ax-cover--lg" aria-hidden="true">🎸</div>
                  <div className="ax-grow">
                    <div className="ax-meta">PLAYLIST · by {pl.owner?.displayName || 'A SoundDAO fan'}</div>
                    <h1 className="ax-h1">{pl.title}</h1>
                    <div className="ax-meta">
                      {[pl.description, `${pl.trackCount} track${pl.trackCount === 1 ? '' : 's'}`, totalSec ? `${Math.round(totalSec / 60)} min` : ''].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                </div>
                <div className="ax-actions">
                  <button type="button" className="ax-btn ax-btn--primary" onClick={() => queue.length && playQueue(queue, 0)} disabled={!queue.length}>▶ Play all</button>
                </div>
                {pl.tracks.map((t, i) => {
                  const on = isCurrent(queue[i]?.key);
                  const playing = on && (status === 'playing' || status === 'loading');
                  return (
                    <div className="ax-track" key={t.id}>
                      <span className="ax-track-n">{i + 1}</span>
                      <button type="button" className={`ax-play ${on ? 'on' : ''}`} onClick={() => (on ? toggle() : playQueue(queue, i))} aria-label={playing ? `Pause ${t.title}` : `Play ${t.title}`}>
                        {playing ? '❚❚' : '▶'}
                      </button>
                      <div className="ax-grow">
                        <div className="ax-title">{t.title || t.fileName}</div>
                        <div className="ax-meta">{t.artistDisplay} · <a href={t.archiveUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#a5b4fc', textDecoration: 'none' }}>archive.org ↗</a></div>
                      </div>
                      <span className="ax-dur">{formatDuration(t.durationSec)}</span>
                    </div>
                  );
                })}
                <p className="ax-attr">Streaming from the Internet Archive</p>
              </div>
              <div className="ax-cta">
                <div className="ax-title" style={{ fontSize: 17, whiteSpace: 'normal' }}>Dig with us — build playlists, find the next band first.</div>
                <p className="ax-meta" style={{ margin: '6px 0 12px' }}>Earn SOUND for sharing, and earn more when a band you found joins.</p>
                <Link to={session ? '/dig' : '/login'} className="ax-btn ax-btn--primary">{session ? 'Dig the Archive' : 'Join SoundDAO'}</Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
